import React, { useState, useEffect, useRef, Fragment } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../config/api';
import {
  XMarkIcon, TrashIcon, ShieldCheckIcon,
  ChevronUpDownIcon, CheckIcon,
} from '@heroicons/react/24/outline';

interface UserSearchResult {
  id: string;
  email: string;
  full_name: string | null;
}

interface ManageSubAdminsModalProps {
  institutionId: string;
  onClose: () => void;
  // Fired after a successful add/remove so the parent page (which renders
  // its own staff list inline) can refetch and the user sees the change
  // without closing/reopening the modal.
  onChanged?: () => void;
}

interface MemberInfo {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  is_active: boolean;
  joined_at: string;
}

const ManageSubAdminsModal: React.FC<ManageSubAdminsModalProps> = ({ institutionId, onClose, onChanged }) => {
  const { isDarkMode } = useTheme();
  const [subAdmins, setSubAdmins] = useState<MemberInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  // Typeahead state — same shape as ManageAdminsModal so picking a user
  // from a debounced server search avoids the typo-prone freeform email.
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<UserSearchResult | null>(null);
  const debounceRef = useRef<number | null>(null);

  const fetchSubAdmins = async () => {
    try {
      const response = await api.get(`/api/institutions/${institutionId}/members`);
      setSubAdmins(response.data.filter((m: MemberInfo) => m.role === 'institution_sub_admin'));
    } catch (error) {
      console.error('Failed to fetch sub-admins:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubAdmins();
  }, [institutionId]);

  // Debounced server search — 250ms / min 2 chars to match the backend
  // Query() validation. Aborts on each keystroke via clearTimeout.
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = window.setTimeout(async () => {
      try {
        const response = await api.get(
          `/api/institutions/${institutionId}/users/search`,
          { params: { q: trimmed, limit: 10 } }
        );
        setResults(response.data);
      } catch (err) {
        console.error('User search failed:', err);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [query, institutionId]);

  const handleAdd = async () => {
    if (!selected) return;
    setAdding(true);
    setError('');
    try {
      await api.post(`/api/institutions/${institutionId}/admins`, {
        email: selected.email,
      });
      setSelected(null);
      setQuery('');
      setResults([]);
      fetchSubAdmins();
      onChanged?.();
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to add sub-admin');
    } finally {
      setAdding(false);
    }
  };

  // Existing-sub-admin emails for in-dropdown disable hint.
  const existingEmails = new Set(subAdmins.map(a => a.email.toLowerCase()));

  const handleRemove = async (userId: string) => {
    if (!window.confirm('Remove this sub-admin?')) return;
    try {
      await api.delete(`/api/institutions/${institutionId}/admins/${userId}`);
      fetchSubAdmins();
      onChanged?.();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to remove sub-admin');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className={`w-full max-w-md mx-4 p-6 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            <ShieldCheckIcon className="h-5 w-5 text-cyan-500" />
            Manage Sub-Admins
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Add new */}
        <div className="flex gap-2 mb-4">
          <Combobox
            value={selected}
            onChange={(value: UserSearchResult | null) => setSelected(value)}
          >
            <div className="relative flex-1">
              <Combobox.Input
                className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${
                  isDarkMode
                    ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400 focus:border-cyan-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-cyan-500'
                }`}
                displayValue={(u: UserSearchResult | null) => u?.email ?? ''}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by email or name (min 2 chars)"
                autoComplete="off"
              />
              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
              </Combobox.Button>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                afterLeave={() => setResults([])}
              >
                <Combobox.Options
                  className={`absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-lg py-1 text-sm shadow-lg ring-1 ring-black/5 ${
                    isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-900'
                  }`}
                >
                  {searching ? (
                    <div className={`px-3 py-2 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      Searching…
                    </div>
                  ) : query.trim().length < 2 ? (
                    <div className={`px-3 py-2 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      Type at least 2 characters
                    </div>
                  ) : results.length === 0 ? (
                    <div className={`px-3 py-2 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      No users match &ldquo;{query}&rdquo;
                    </div>
                  ) : (
                    results.map((u) => {
                      const isExisting = existingEmails.has(u.email.toLowerCase());
                      return (
                        <Combobox.Option
                          key={u.id}
                          value={u}
                          disabled={isExisting}
                          className={({ active, disabled }) =>
                            `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                              disabled
                                ? 'opacity-50 cursor-not-allowed'
                                : active
                                  ? 'bg-cyan-500 text-white'
                                  : isDarkMode ? 'text-slate-200' : 'text-gray-900'
                            }`
                          }
                        >
                          {({ selected: optSelected }) => (
                            <>
                              <div className="truncate font-medium">
                                {u.full_name || u.email}
                              </div>
                              {u.full_name && (
                                <div className="truncate text-xs opacity-75">{u.email}</div>
                              )}
                              {isExisting && (
                                <div className="truncate text-xs opacity-75 italic">
                                  Already a sub-admin
                                </div>
                              )}
                              {optSelected && !isExisting && (
                                <span className="absolute inset-y-0 right-0 flex items-center pr-3">
                                  <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </Combobox.Option>
                      );
                    })
                  )}
                </Combobox.Options>
              </Transition>
            </div>
          </Combobox>
          <button
            onClick={handleAdd}
            disabled={adding || !selected}
            className="px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}
          >
            {adding ? '...' : 'Add'}
          </button>
        </div>
        {error && <p className="text-red-400 text-sm mb-3">{error}</p>}

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-500"></div>
          </div>
        ) : subAdmins.length === 0 ? (
          <p className={`text-center py-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            No sub-admins yet.
          </p>
        ) : (
          <div className="space-y-2">
            {subAdmins.map((admin) => (
              <div
                key={admin.id}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  isDarkMode ? 'bg-slate-700/50' : 'bg-gray-50'
                }`}
              >
                <div>
                  <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {admin.full_name || admin.email}
                  </div>
                  {admin.full_name && (
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {admin.email}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleRemove(admin.user_id)}
                  className="text-red-400 hover:text-red-300 p-1"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg text-sm ${
              isDarkMode ? 'text-gray-400 hover:bg-slate-700' : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageSubAdminsModal;
